// User-mission API service.
//
// Talks to the User-App-facing SelfieBooth endpoints (separate from Admin's
// /admin/user-missions CRUD) so the authenticated Dealzta user's mission
// progress is tracked server-side. Requests go through apiClient so the
// Dealzta bearer token is attached — these endpoints require it. user_id is
// never sent; the backend derives the acting user from the token alone.

import { fetchJson } from './apiClient';

/**
 * Fetch the authenticated user's own missions (GET /user-missions).
 * Resolves to the raw array (may be empty).
 */
export async function getUserMissions() {
  const data = await fetchJson('/user-missions');
  return Array.isArray(data) ? data : [];
}

/**
 * Start (or idempotently retrieve) the authenticated user's mission for a
 * given shop (POST /user-missions/start). Never sends user_id — identity
 * comes exclusively from the authenticated token.
 */
export async function startUserMission(journeyId, shopId) {
  return fetchJson('/user-missions/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ journey_id: journeyId, shop_id: shopId }),
  });
}

/**
 * Report that a mission step has just been completed
 * (PATCH /user-missions/{id}/progress). Sends the step that finished, never
 * a target current_step — the backend alone decides/validates the next
 * value (rejects skipping ahead, no-ops on a repeat/stale call).
 */
export async function completeMissionStep(userMissionId, completedStep, location = null) {
  const payload = { completed_step: completedStep };

  if (
    location &&
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng)
  ) {
    payload.latitude = location.lat;
    payload.longitude = location.lng;
  }

  return fetchJson(`/user-missions/${userMissionId}/progress`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Report that the Post step has just been completed
 * (PATCH /user-missions/{id}/post). This is the sole authoritative
 * Post-completion call — the generic completeMissionStep()/`/progress`
 * above is no longer used for step 3. Sends no body: posted_at and
 * current_step are both decided by the backend alone, never here.
 */
export async function completeMissionPost(userMissionId) {
  return fetchJson(`/user-missions/${userMissionId}/post`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Award the fixed +1 point for a completed shop mission
 * (POST /user-missions/{id}/points). Sends no body — the point amount and
 * the acting user are both decided by the backend alone, never here.
 * Resolves to { point_transaction, user_mission, progress: { completed_shops,
 * places_required, journey_completed } }; the backend's `progress` is the
 * source of truth for how many shops this journey has completed, not any
 * frontend-tracked count.
 */
export async function awardMissionPoint(userMissionId) {
  return fetchJson(`/user-missions/${userMissionId}/points`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}
